/**
 * GroupOpsMenu — admin-only "Manage" dropdown on the group detail page.
 *
 *   • Split into a new group  — move selected members out (same company)
 *   • Merge groups into this  — absorb other same-company groups
 *   • Transfer to another company — re-home tenant_id (+ optional
 *     compressor ownership transfer for duplicate-company cleanup)
 *
 * Backend enforces admin; this component additionally hides itself for
 * non-admins via /sequencing/system/status.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DotsThreeVertical, ArrowsSplit, ArrowsMerge, Swap, Spinner, X,
  Warning, MagnifyingGlass, PencilSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  seqStatus, seqListGroups, seqSplitGroup, seqMergeGroups, seqTransferGroup,
  seqPatchGroup,
} from "@/lib/sequencingApi";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

function errDetail(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return { message: d };
  if (d && typeof d === "object") return d;
  return { message: e?.message || String(e) };
}

function ErrorBox({ detail, testid }) {
  if (!detail) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-800 space-y-1"
         data-testid={testid}>
      <div className="font-bold flex items-start gap-1.5">
        <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
        <span>{detail.message}</span>
      </div>
      {detail.blocked_groups?.length > 0 && (
        <ul className="pl-5 list-disc">
          {detail.blocked_groups.map(b => (
            <li key={b.id}>
              <span className="font-bold">{b.name || b.id}</span>
              {" — "}{b.state}{b.engine_enabled ? " (engine enabled)" : ""}
            </li>
          ))}
        </ul>
      )}
      {detail.how_to_fix && (
        <p className="text-red-700 bg-red-100/70 rounded-sm p-2">
          <span className="font-bold uppercase tracking-wider text-[10px]">How to fix: </span>
          {detail.how_to_fix}
        </p>
      )}
    </div>
  );
}

function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-3"
         data-testid={testid}>
      <div className="bg-white rounded-sm w-full max-w-md p-5 space-y-3 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold font-['Chivo'] text-slate-900 flex-1">{title}</h2>
          <button type="button" onClick={onClose} data-testid={`${testid}-close`}
                  className="w-7 h-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400">
            <X size={14} weight="bold" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Rename ───────────────────────────────────────────────────────────
function RenameModal({ group, onClose, onChanged }) {
  const [name, setName] = useState(group?.name || "");
  const [busy, setBusy] = useState(false);
  const [errD, setErrD] = useState(null);

  async function submit(e) {
    e?.preventDefault?.();
    const next = name.trim();
    if (!next || next === (group?.name || "").trim()) { onClose(); return; }
    setBusy(true); setErrD(null);
    try {
      await seqPatchGroup(group.id, { name: next });
      toast.success(`Renamed to "${next}"`);
      onChanged?.();
      onClose();
    } catch (e2) {
      setErrD(errDetail(e2));
    } finally { setBusy(false); }
  }

  return (
    <ModalShell title="Rename group" onClose={onClose} testid="seq-rename-modal">
      <p className="text-xs text-slate-500">
        Choose a new display name. Must be unique within the company.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
            Group name
          </span>
          <input value={name} onChange={e => setName(e.target.value)} required autoFocus
                 data-testid="seq-rename-field-name"
                 className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
        </label>
        <ErrorBox detail={errD} testid="seq-rename-error" />
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose}
                  className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit"
                  disabled={busy || !name.trim() || name.trim() === (group?.name || "").trim()}
                  data-testid="seq-rename-submit"
                  className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Spinner size={12} className="animate-spin" /> : <PencilSimple size={12} weight="bold" />}
            Save
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── Split ────────────────────────────────────────────────────────────
function SplitModal({ group, onClose }) {
  const navigate = useNavigate();
  const members = group.members || [];
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [errD, setErrD] = useState(null);

  const toggle = (did) => setSelected(prev => {
    const n = new Set(prev);
    n.has(did) ? n.delete(did) : n.add(did);
    return n;
  });
  const allSelected = selected.size >= members.length && members.length > 0;

  async function submit(e) {
    e?.preventDefault?.();
    setBusy(true); setErrD(null);
    try {
      const res = await seqSplitGroup(group.id, {
        name: name.trim(), device_ids: [...selected],
      });
      toast.success(`Split done — ${res.moved} member(s) moved to "${res.new_group.name}"`);
      (res.warnings || []).forEach(w => toast.warning(w, { duration: 8000 }));
      navigate(`/sequencing/groups/${res.new_group.id}`);
    } catch (e2) {
      setErrD(errDetail(e2));
    } finally { setBusy(false); }
  }

  return (
    <ModalShell title="Split into a new group" onClose={onClose} testid="seq-split-modal">
      <p className="text-xs text-slate-500">
        Selected compressors move into a brand-new group under the same
        company. Settings (Pset, bandwidth, delays) are inherited, but the
        new group starts idle and needs its own commissioning.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
            New group name
          </span>
          <input value={name} onChange={e => setName(e.target.value)} required autoFocus
                 data-testid="seq-split-field-name"
                 className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
        </label>
        <div>
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
            Members to move ({selected.size}/{members.length})
          </span>
          <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-52 overflow-y-auto"
              data-testid="seq-split-member-list">
            {members.map(m => (
              <li key={m.device_id}>
                <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={selected.has(m.device_id)}
                         onChange={() => toggle(m.device_id)}
                         data-testid={`seq-split-member-${m.device_id}`} />
                  <span className="font-mono text-xs">{m.serial_number || m.device_id}</span>
                </label>
              </li>
            ))}
            {members.length === 0 && (
              <li className="px-3 py-2 text-xs text-slate-400 italic">No members in this group</li>
            )}
          </ul>
          {allSelected && (
            <p className="text-[11px] text-amber-700 mt-1">
              At least one compressor must stay in the source group.
            </p>
          )}
        </div>
        <ErrorBox detail={errD} testid="seq-split-error" />
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose}
                  className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit"
                  disabled={busy || !name.trim() || selected.size === 0 || allSelected}
                  data-testid="seq-split-submit"
                  className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Spinner size={12} className="animate-spin" /> : <ArrowsSplit size={12} weight="bold" />}
            Split
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── Merge ────────────────────────────────────────────────────────────
function MergeModal({ group, onClose, onChanged }) {
  const [candidates, setCandidates] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [errD, setErrD] = useState(null);

  useEffect(() => {
    seqListGroups(group.tenant_id)
      .then(d => setCandidates((d.items || []).filter(g => g.id !== group.id)))
      .catch(e => { setCandidates([]); setErrD(errDetail(e)); });
  }, [group.id, group.tenant_id]);

  const toggle = (gid) => setSelected(prev => {
    const n = new Set(prev);
    n.has(gid) ? n.delete(gid) : n.add(gid);
    return n;
  });

  async function submit() {
    setBusy(true); setErrD(null);
    try {
      const res = await seqMergeGroups(group.id, { source_group_ids: [...selected] });
      toast.success(`Merged — ${res.moved} member(s) moved into "${group.name}"`
        + (res.skipped_duplicates?.length ? `, ${res.skipped_duplicates.length} duplicate(s) skipped` : ""));
      onChanged?.();
      onClose();
    } catch (e2) {
      setErrD(errDetail(e2));
    } finally { setBusy(false); }
  }

  return (
    <ModalShell title={`Merge groups into "${group.name}"`} onClose={onClose} testid="seq-merge-modal">
      <p className="text-xs text-slate-500">
        All members of the selected groups move into this group, and the
        selected groups are deleted. This group keeps its own settings.
        Only same-company groups can be merged.
      </p>
      {candidates === null ? (
        <div className="py-6 text-center"><Spinner size={16} className="mx-auto animate-spin text-slate-400" /></div>
      ) : candidates.length === 0 ? (
        <p className="text-xs text-slate-400 italic" data-testid="seq-merge-empty">
          No other groups exist for this company.
        </p>
      ) : (
        <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-52 overflow-y-auto"
            data-testid="seq-merge-candidate-list">
          {candidates.map(g => (
            <li key={g.id}>
              <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={selected.has(g.id)}
                       onChange={() => toggle(g.id)}
                       data-testid={`seq-merge-candidate-${g.id}`} />
                <span className="flex-1 min-w-0 truncate font-medium">{g.name}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold shrink-0
                                  ${g.state === "auto_sequencing" ? "text-emerald-700" : "text-slate-400"}`}>
                  {g.state}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <ErrorBox detail={errD} testid="seq-merge-error" />
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={onClose}
                className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={busy || selected.size === 0}
                data-testid="seq-merge-submit"
                className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
          {busy ? <Spinner size={12} className="animate-spin" /> : <ArrowsMerge size={12} weight="bold" />}
          Merge {selected.size > 0 ? `${selected.size} group(s)` : ""}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Transfer ─────────────────────────────────────────────────────────
function TransferModal({ group, onClose, onChanged }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null); // {id, name}
  const [moveOwnership, setMoveOwnership] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errD, setErrD] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!search.trim() || search.trim().length < 2) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      api.get("/customers", { params: { search: search.trim(), per_page: 15, country: "" } })
        .then(r => setResults(r.data?.items || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  async function submit() {
    setBusy(true); setErrD(null);
    try {
      const res = await seqTransferGroup(group.id, {
        target_tenant_id: target.id,
        transfer_compressor_ownership: moveOwnership,
      });
      const okN = (res.compressor_transfers || []).filter(t => t.ok).length;
      const failN = (res.compressor_transfers || []).filter(t => !t.ok).length;
      toast.success(`Group transferred to ${res.new_tenant_name}`
        + (moveOwnership ? ` — ${okN} compressor(s) re-owned` : ""));
      if (failN > 0) toast.error(`${failN} compressor transfer(s) failed — check the group and retry per unit.`, { duration: 9000 });
      if (res.devices_without_serial?.length > 0) {
        toast.warning(`${res.devices_without_serial.length} device(s) have no linked serial — ownership unchanged for those.`, { duration: 9000 });
      }
      onChanged?.();
      onClose();
    } catch (e2) {
      setErrD(errDetail(e2));
    } finally { setBusy(false); }
  }

  return (
    <ModalShell title="Transfer to another company" onClose={onClose} testid="seq-transfer-modal">
      <p className="text-xs text-slate-500">
        Re-homes this group (and optionally its compressors' ownership) to a
        different company — useful when the same company exists twice under
        different names.
      </p>
      <div className="text-[11px] bg-slate-50 border border-slate-200 rounded-sm p-2">
        Current company: <span className="font-bold">{group.customer_name || group.tenant_id}</span>
      </div>
      <div>
        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
          Target company
        </span>
        {target ? (
          <div className="flex items-center gap-2 border border-blue-300 bg-blue-50 rounded-sm px-3 py-2 text-sm"
               data-testid="seq-transfer-target-selected">
            <span className="flex-1 min-w-0 truncate font-bold">{target.name}</span>
            <span className="font-mono text-[10px] text-slate-500">{target.id}</span>
            <button type="button" onClick={() => setTarget(null)}
                    data-testid="seq-transfer-target-clear"
                    className="text-slate-400 hover:text-slate-700"><X size={12} weight="bold" /></button>
          </div>
        ) : (
          <>
            <div className="relative">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                     placeholder="Search company name…"
                     data-testid="seq-transfer-search"
                     className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
            </div>
            {searching && <p className="text-[11px] text-slate-400 mt-1">Searching…</p>}
            {results.length > 0 && (
              <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-40 overflow-y-auto mt-1"
                  data-testid="seq-transfer-results">
                {results.filter(c => c.CustomerID !== group.tenant_id).map(c => (
                  <li key={c.CustomerID}>
                    <button type="button"
                            onClick={() => setTarget({ id: c.CustomerID, name: c["Company Name"] || c.CustomerID })}
                            data-testid={`seq-transfer-result-${c.CustomerID}`}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2">
                      <span className="flex-1 min-w-0 truncate">{c["Company Name"] || "—"}</span>
                      <span className="font-mono text-[10px] text-slate-400">{c.CustomerID}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
        <input type="checkbox" checked={moveOwnership}
               onChange={e => setMoveOwnership(e.target.checked)}
               data-testid="seq-transfer-ownership-checkbox"
               className="mt-0.5" />
        <span>
          Also transfer <span className="font-bold">compressor ownership</span> of
          this group's {group.members?.length || 0} member unit(s) to the target
          company (updates Owner Company on each serial).
        </span>
      </label>
      <ErrorBox detail={errD} testid="seq-transfer-error" />
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={onClose}
                className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={busy || !target}
                data-testid="seq-transfer-submit"
                className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5">
          {busy ? <Spinner size={12} className="animate-spin" /> : <Swap size={12} weight="bold" />}
          Transfer
        </button>
      </div>
    </ModalShell>
  );
}

// ── Menu ─────────────────────────────────────────────────────────────
export const GroupOpsMenu = ({ group, onChanged }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [modal, setModal] = useState(null); // "split" | "merge" | "transfer"

  useEffect(() => {
    let alive = true;
    seqStatus().then(s => alive && setIsAdmin(!!s?.is_admin)).catch(() => {});
    return () => { alive = false; };
  }, []);

  const items = useMemo(() => ([
    { key: "rename",   label: "Rename group…",                Icon: PencilSimple },
    { key: "split",    label: "Split into a new group…",      Icon: ArrowsSplit },
    { key: "merge",    label: "Merge groups into this…",      Icon: ArrowsMerge },
    { key: "transfer", label: "Transfer to another company…", Icon: Swap },
  ]), []);

  if (!isAdmin || !group) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" data-testid="seq-group-ops-menu"
                  title="Manage group (split / merge / transfer)"
                  className="ml-1 w-8 h-8 inline-flex items-center justify-center rounded-sm border border-slate-300 bg-white text-slate-600 hover:bg-slate-50">
            <DotsThreeVertical size={16} weight="bold" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {items.map(({ key, label, Icon }) => (
            <DropdownMenuItem key={key} data-testid={`seq-group-ops-${key}`}
                              onClick={() => setModal(key)}
                              className="text-xs font-medium gap-2 cursor-pointer">
              <Icon size={14} weight="bold" /> {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {modal === "rename" && (
        <RenameModal group={group} onClose={() => setModal(null)} onChanged={onChanged} />
      )}
      {modal === "split" && (
        <SplitModal group={group} onClose={() => setModal(null)} />
      )}
      {modal === "merge" && (
        <MergeModal group={group} onClose={() => setModal(null)} onChanged={onChanged} />
      )}
      {modal === "transfer" && (
        <TransferModal group={group} onClose={() => setModal(null)} onChanged={onChanged} />
      )}
    </>
  );
};

export default GroupOpsMenu;
